import { PaywallResponse } from '@/types/paywall';

// Global paywall handler - will be set by PaywallProvider
let globalPaywallHandler: ((config: PaywallResponse['paywallConfig']) => void) | null = null;

export const setGlobalPaywallHandler = (handler: (config: PaywallResponse['paywallConfig']) => void) => {
  globalPaywallHandler = handler;
};

export const clearGlobalPaywallHandler = () => {
  globalPaywallHandler = null;
};

// Function to check if response is a paywall response
export const isPaywallResponse = (response: any): response is PaywallResponse => {
  return response && 
         response.status === 'paywall' && 
         response.statusCode === 402 && 
         response.action === 'paywall' && 
         response.paywallConfig;
};

// Function to handle paywall response
export const handlePaywallResponse = (response: PaywallResponse) => {
  if (globalPaywallHandler) {
    globalPaywallHandler(response.paywallConfig);
  } else {
    console.warn('Paywall response received but no handler is set');
  }
};

// Generic function to intercept and handle 402 responses
export const interceptPaywallResponse = async <T>(
  apiCall: () => Promise<T>
): Promise<T> => {
  try {
    const result = await apiCall();
    
    // Check if the result is a paywall response
    if (isPaywallResponse(result)) {
      console.log('✅ Paywall response detected in result:', result);
      handlePaywallResponse(result);
      throw new Error(result.message || 'Funcionalidade requer plano premium');
    }
    
    return result;
  } catch (error: any) {
    console.log('🔍 Intercepting error:', error);
    
    // If it's a fetch error with status 402, try to parse the paywall response
    if (error.status === 402 || error.statusCode === 402) {
      console.log('🚫 402 error detected, checking for paywall config...');
      
      try {
        let paywallData;
        
        // Check if error already has paywallConfig (from our modified sendMessage)
        if (typeof error === 'object' && error.paywallConfig) {
          paywallData = error;
          console.log('✅ Found paywallConfig in error object:', paywallData.paywallConfig);
        } else if (error.body) {
          paywallData = typeof error.body === 'string' ? JSON.parse(error.body) : error.body;
          console.log('✅ Parsed paywallConfig from error.body:', paywallData);
        }
        
        if (paywallData && isPaywallResponse(paywallData)) {
          console.log('🎯 Valid paywall response found, opening modal...');
          handlePaywallResponse(paywallData);
          throw new Error(paywallData.message || 'Funcionalidade requer plano premium');
        } else {
          console.warn('❌ No valid paywall config found in 402 response');
        }
      } catch (parseError) {
        // If we can't parse the paywall data, fall back to generic error
        console.warn('❌ Failed to parse paywall response:', parseError);
      }
    }
    
    throw error;
  }
};